"use client";

import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";
import { Navigation, Autoplay, EffectFade } from "swiper/modules";
import Link from 'next/link';
import Title from "../../components/title";
import ArrowDown from "../../components/svg/arrow-dwon";
import ArrowUp from "../../components/svg/arrow-up";
import One from "../../components/svg/one";
import Two from "../../components/svg/two";
import Tree from "../../components/svg/tree";
import Four from "../../components/svg/four";
import LearnIcon from "../../components/svg/learnIcon";
import {getStrapiURL} from "../../../helper/image-helper";

interface OfferSliderProps {
    title: string; 
    slider: Array<{
        link: string;
        linkText: string;
        title: string;
        text: string;
        image: {
            url: string;
        };
        icon: {
            url: string;
        };
    }>
}

const numberComponents = [<One key={1} />, <Two key={2} />, <Tree key={3} />, <Four key={4} />];

const OfferSlider: React.FC<OfferSliderProps> = ({ title, slider }) =>  {

    const [activeSlide, setActiveSlide] = useState<number>(0);

    const handleTextClick = (index: number): void => {
        setActiveSlide(index);
    };

    const handleDotClick = (index: number): void => {
        setActiveSlide(index);
    };

    return (
        <div className="w-full">
            <div className="flex justify-between items-center p-4 max-[767px]:justify-center">
                <h2 className="text-lg font-bold">
                    <Title text={title} position="center" />
                </h2>
                <div className="flex space-x-2 max-[767px]:hidden">
                    <button
                        className="flex justify-center items-center w-[56px] h-[56px] px-4 rounded-full border-[2px] border-violet-50 hover:bg-violet-50"
                        onClick={() =>
                            setActiveSlide(
                                (prev) => (prev + 1) % slider.length
                            )
                        }
                    >
                        <ArrowDown />
                    </button>
                    <button
                        className="flex justify-center items-center w-[56px] h-[56px] px-4 rounded-full border-[2px] border-violet-50 hover:bg-violet-50 "
                        onClick={() => setActiveSlide(
                            (prev) => (prev - 1 + slider.length) % slider.length
                        )}
                    >
                        <ArrowUp />
                    </button>
                </div>
            </div>

            <div className="flex border-[8px] mt-[40px] rounded-[64px] p-[56px] pb-[120px] gap-[56px] bg-white 
                max-[1200px]:gap-[30px] 
                max-[1024px]:flex-wrap 
                max-[767px]:gap-[16px] 
                max-[576px]:mt-[26px] 
                max-[576px]:p-[20px] 
                max-[576px]:border-[4px]
                max-[576px]:rounded-[24px]
            ">
                <div className="w-1/2 flex items-center justify-center relative h-[584px] max-[1200px]:h-[460px] max-[1024px]:w-full max-[576px]:rounded-[16px] max-[576px]:h-[212px]">
                    {slider.map((slide, index) => (
                        <div
                            key={index}
                            className={`absolute top-0 left-0 w-full h-full transition-opacity duration-1000 ease-in-out ${activeSlide === index ? "opacity-100" : "opacity-0"}`}
                        >
                            <img
                                src={getStrapiURL(slide.image.url)}
                                alt={`Slide ${index + 1}`}
                                className="w-full h-full object-cover rounded-[40px] max-[576px]:rounded-[16px]"
                            />
                            <div className="absolute bottom-[-64px] left-[52px]  max-[576px]:left-[24px] ">
                                <img
                                    src={getStrapiURL(slide.icon.url)}
                                    alt="Icon"
                                    className="w-[128px] h-[128px] max-[1200px]:w-[64px] max-[576px]:w-[56px]"
                                />
                            </div>
                            <div className="absolute bottom-[32px] right-[32px] max-[576px]:bottom-[8px] max-[576px]:right-[8px]">
                                <Link href={slide.link} className="flex p-2 pl-4 items-center gap-4 text-violet-500 font-bold  uppercase bg-white rounded-[12px] max-[576px]:text-[12px] max-[576px]:p-[4px] max-[576px]:pl-[8px]">
                                    <span>{slide.linkText}</span>
                                    <LearnIcon />
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="hidden justify-center space-x-2 mt-4 w-full max-[767px]:flex">
                    {slider.map((_, index) => (
                        <button
                            key={index}
                            className={`w-[16px] h-[16px] rounded-full border-[1.5px] ${activeSlide === index ? "bg-[#E8E9EF]" : "bg-white"}`}
                            onClick={() => handleDotClick(index)}
                        />
                    ))}
                </div>
                <div className="w-1/2 flex flex-col justify-center max-[1024px]:w-full max-[1024px]:mt-[16px] max-[767px]:mt-0">
                    {slider.map((block, index) => (
                        <div
                            key={index}
                            className={`
                                flex gap-[32px] 
                                justify-between 
                                relative 
                                p-8 
                                max-[1200px]:p-4 
                                max-[1024px]:p-8   
                                max-[767px]:p-0 
                                max-[767px]:border-none
                                max-[767px]:shadow-none
                                border-b-2 
                                cursor-pointer 
                                ${index === activeSlide ? "rounded-[32px] shadow-[0_4px_16px_0_#00000026] border-2 border-solid border-violet-50" : "border-dotted"} max-[767px]:${index === activeSlide ? "block" : "hidden"}
                            `}
                            onClick={() => handleTextClick(index)}
                        >
                            {index === activeSlide && (
                                <div className="w-[17px] h-[42px] absolute left-[-9px] -translate-x-1/2 top-1/2 -translate-y-1/2 max-[767px]:hidden" style={{ backgroundImage: "url('/template/left-item.svg')" }}></div>
                            )}
                            <div>
                                <h3 className="
                                text-violet-500 
                                leading-[125%] 
                                font-gilroy 
                                mb-[8px] 
                                font-bold 
                                text-[24px]  
                                max-[1200px]:text-[22px] 
                                max-[767px]:text-[18px] 
                                max-[576px]:mb-[4px] 
                            ">{block.title}</h3>
                                <p className={`text-lg font-gilroy leading-[22px] font-medium max-[1200px]:text-[16px] max-[576px]:text-[14px] ${index === activeSlide ? "text-violet-400" : "text-gray-800"}`}>{block.text}</p>
                            </div>
                            <span className="text-gray-500 max-[767px]:hidden">
                                {numberComponents[index % numberComponents.length]}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex justify-center  space-x-2 min-[767px]:hidden max-[767px]:mt-[24px]">
                <button
                    className="flex rotate-90 justify-center items-center w-[40px] h-[40px] px-2 rounded-full border-[2px] border-violet-50 hover:bg-violet-50"
                    onClick={() =>
                        setActiveSlide(
                            (prev) => (prev + 1) % slider.length
                        )
                    }
                >
                    <ArrowDown />
                </button>
                <button
                    className="flex rotate-90 justify-center items-center w-[40px] h-[40px] px-2 rounded-full border-[2px] border-violet-50 hover:bg-violet-50 "
                    onClick={() => setActiveSlide(
                        (prev) => (prev - 1 + slider.length) % slider.length
                    )}
                >
                    <ArrowUp />
                </button>
            </div>
        </div>
    );
};

export default OfferSlider;